import { useState, useEffect } from 'react';
import { useLocation, matchPath } from 'react-router-dom';

// material-ui
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';

// project imports
import NavItem from './NavItem';
import { useGetMenuMaster, handlerDrawerOpen } from '../../../../../api/menu';

// assets
import { DownOutlined, RightOutlined } from '@ant-design/icons';

// ==============================|| NAVIGATION - LIST COLLAPSE ||============================== //

import { Theme, alpha } from '@mui/material/styles';

interface NavCollapseProps {
  item: any;
  level: number;
}

export default function NavCollapse({ item, level }: NavCollapseProps) {
  const { t } = useTranslation();
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened || false;
  const collapseAllMenus = menuMaster?.collapseAllMenus;

  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // Efecto para colapsar todos los menús cuando se cierra el drawer
  useEffect(() => {
    if (collapseAllMenus) {
      setOpen(false);
    }
  }, [collapseAllMenus]);

  const { children, id, icon, title } = item;
  const label = item?.titleKey ? t(item.titleKey) : title;
  const showTooltip = !drawerOpen && level === 1;

  const Icon = icon as any;
  const itemIcon = icon ? (
    <Icon
      style={{
        fontSize: drawerOpen ? '1rem' : '1.25rem'
      }}
    />
  ) : null;

  // Check if any child is selected
  const isSelected = children?.some((child: any) => {
    if (child.url) {
      return !!matchPath({ path: child.url, end: false }, pathname);
    }
    return false;
  });

  const textColor = 'text.primary';
  const iconSelectedColor = 'primary.contrastText';

  const handleClick = () => {
    // Si el drawer estÃ¡ cerrado, abrirlo primero
    if (!drawerOpen) {
      setOpen(false);
      handlerDrawerOpen(true);
      return;
    }
    
    // Si el drawer estÃ¡ abierto, alternar el estado del collapse
    setOpen(!open);
  };

  // menu list collapse & items
  const items = children?.map((menuItem: any) => {
    switch (menuItem.type) {
      case 'collapse':
        return <NavCollapse key={menuItem.id} item={menuItem} level={level + 1} />;
      case 'item':
        return <NavItem key={menuItem.id} item={menuItem} level={level + 1} />;
      default:
        return (
          <Typography key={menuItem.id} variant="h6" color="error" align="center">
            Fix - Group Collapse or Items
          </Typography>
        );
    }
  });

  return (
    <>
      <Tooltip title={showTooltip ? label : ''} placement="right" arrow disableHoverListener={!showTooltip}>
        <Box component="span" sx={{ display: 'block' }}>
          <ListItemButton
            sx={(theme: any) => ({
              mb: 0.5,
              py: level > 1 ? 1 : 1.25,
              pl: drawerOpen ? `${level * 14}px` : 1.5,
              transition: 'background-color 150ms ease, color 150ms ease, border-color 150ms ease',
              ...(drawerOpen && {
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.light, 0.15),
                  ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.primary.dark, 0.2) })
                },
                ...(isSelected && {
                  position: 'relative',
                  bgcolor: 'primary.main',
                  borderRight: '2px solid',
                  borderColor: 'primary.dark',
                  color: iconSelectedColor,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 6,
                    bottom: 6,
                    width: 3,
                    borderRadius: 2,
                    bgcolor: 'primary.dark',
                    transition: 'background-color 150ms ease'
                  },
                  '&:hover': { color: iconSelectedColor, bgcolor: 'primary.dark', '&::before': { bgcolor: 'primary.dark' } }
                })
              }),
              ...(!drawerOpen && {
                '&:hover': { bgcolor: 'transparent' },
                ...(isSelected && { '&:hover': { bgcolor: 'transparent' }, bgcolor: 'transparent' })
              })
            })}
            onClick={handleClick}
          >
        {itemIcon && (
          <ListItemIcon
            sx={(theme: any) => ({
              minWidth: 28,
              color: isSelected ? iconSelectedColor : textColor,
              ...(drawerOpen && {
                borderRadius: 1.5,
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.light, 0.15),
                  ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.primary.dark, 0.2) })
                }
              }),
              ...(!drawerOpen && {
                borderRadius: 1.5,
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.light, 0.15),
                  ...theme.applyStyles('dark', { bgcolor: alpha(theme.palette.primary.dark, 0.2) })
                }
              }),
              ...(!drawerOpen &&
                isSelected && {
                  bgcolor: 'secondary.lighter',
                  ...theme.applyStyles('dark', { bgcolor: 'secondary.light' }),
                  '&:hover': { bgcolor: 'secondary.light' }
                })
            })}
          >
            {itemIcon}
          </ListItemIcon>
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography variant="h6" sx={{ color: isSelected ? iconSelectedColor : textColor }}>
                {label}
              </Typography>
            }
          />
        )}
        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <Box sx={{ ml: 'auto' }}>
            {open ? <DownOutlined style={{ fontSize: '0.75rem' }} /> : <RightOutlined style={{ fontSize: '0.75rem' }} />}
          </Box>
        )}
          </ListItemButton>
        </Box>
      </Tooltip>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List component="div" disablePadding sx={{ position: 'relative' }}>
          {items}
        </List>
      </Collapse>
    </>
  );
}



